// pages/ProductionLog.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import logService from '../services/logService';
import productService from '../services/productService';
import bakingCcpService from '../services/bakingCcpService';
import ingredientInventoryService from '../services/ingredientInventoryService';
import sqfService from '../services/sqfService';
import ProductionTrendChart from '../components/ProductionTrendChart';
import StartVisualInspectionModal from '../components/StartVisualInspectionModal';
import VisualInspectionsList from '../components/VisualInspectionsList';
import { usePermissions } from '../context/PermissionContext';

function ProductionLog() {
  const { hasModule } = usePermissions();
  const canViewSQF = hasModule('ccp.baking.qa');
  const canViewChecklists = hasModule('sqf.checklists.view');

  const [logs, setLogs] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ccpRun, setCcpRun] = useState(null);
  const [receivingDocsMap, setReceivingDocsMap] = useState({});
  const [visualInspections, setVisualInspections] = useState([]);
  const [showVisualInspectionModal, setShowVisualInspectionModal] = useState(false);

  // Products for the dropdown
  const [products, setProducts] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0); // eslint-disable-line no-unused-vars

  const defaultStart = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; };
  const defaultEnd   = () => new Date().toISOString().split('T')[0];

  // Effective filter state (applied on Search)
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate]   = useState(defaultStart);
  const [endDate, setEndDate]       = useState(defaultEnd);
  const [productId, setProductId]   = useState('');

  // Input state (live, before Apply)
  const [inputSearch, setInputSearch]       = useState('');
  const [inputStartDate, setInputStartDate] = useState(defaultStart);
  const [inputEndDate, setInputEndDate]     = useState(defaultEnd);
  const [inputProductId, setInputProductId] = useState('');

  // Total quantity for the selected product + date range
  const [totalQty, setTotalQty] = useState(null);
  const [totalQtyLoading, setTotalQtyLoading] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: 'production_date',
    direction: 'desc',
  });

  useEffect(() => {
    productService.getProducts().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const data = await logService.getLogs(currentPage, 50, searchTerm, startDate, endDate, productId);
        setLogs(data.logs);
        setTotalPages(data.totalPages);
        setTotalLogs(data.totalLogs);
      } catch (error) {
        console.error('Error loading logs:', error);
      }
    };
    loadLogs();
  }, [currentPage, searchTerm, startDate, endDate, productId]);

  const loadSummary = useCallback(async () => {
    setTotalQtyLoading(true);
    try {
      const data = await logService.getSummary({ productId: inputProductId, startDate: inputStartDate, endDate: inputEndDate });
      setTotalQty(data.totalQuantity);
    } catch { setTotalQty(null); }
    finally { setTotalQtyLoading(false); }
  }, [inputProductId, inputStartDate, inputEndDate]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(inputSearch);
    setStartDate(inputStartDate);
    setEndDate(inputEndDate);
    setProductId(inputProductId);
    setCurrentPage(1);
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setCcpRun(null);
    setReceivingDocsMap({});
    setVisualInspections([]);
    if (canViewChecklists && log.batchId && log.productId) {
      sqfService.getChecklistLogsForBatch(log.batchId, log.productId).then(setVisualInspections).catch(() => setVisualInspections([]));
    }
    logService
      .getIngredientsByLotCode(log.batchId, log.productId)
      .then((data) => setIngredients(data))
      .catch((err) => console.error('Error fetching ingredients:', err));
    if (log.batchId) {
      ingredientInventoryService.getByBatch(log.batchId)
        .then((data) => {
          const map = {};
          (Array.isArray(data) ? data : []).forEach(({ ingredientId, ingredientLotCode, receivingLog }) => {
            if (receivingLog) map[`${ingredientId}:${ingredientLotCode}`] = receivingLog;
          });
          setReceivingDocsMap(map);
        })
        .catch(() => {});
    }
    if (canViewSQF && log.batchId) {
      bakingCcpService.getRunForBatch(log.batchId)
        .then((run) => setCcpRun(run || null))
        .catch(() => setCcpRun(null));
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedLog(null);
    setIngredients([]);
    setCcpRun(null);
    setReceivingDocsMap({});
    setIsModalOpen(false);
  };

  // ── Ingredient deduplication ──────────────────────────────────────────────
  // The DB stores one row per (ingredientId, lotCode). When a run goes through
  // the live baking flow, a MISSING placeholder is created first, then an
  // ENTERED row is added by QA. We want to show:
  //   • All ENTERED and EXCLUDED rows (these have real data)
  //   • MISSING rows ONLY if that ingredient has no ENTERED/EXCLUDED sibling
  //     (i.e. the lot code was genuinely never filled in)
  // This preserves the multiple-lot-codes-per-ingredient feature while hiding
  // the redundant placeholder rows.
  const displayIngredients = useMemo(() => {
    if (!ingredients.length) return [];

    // Find which ingredientIds have at least one non-MISSING row
    const resolvedIds = new Set(
      ingredients
        .filter(i => i.status !== 'MISSING')
        .map(i => i.ingredientId)
    );

    return ingredients.filter(i => {
      if (i.status !== 'MISSING') return true;          // always show ENTERED / EXCLUDED
      return !resolvedIds.has(i.ingredientId);           // only show MISSING if no resolved sibling
    });
  }, [ingredients]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedLogs = useMemo(() => {
    const clone = [...logs];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    return clone.sort((a, b) => {
      let aVal, bVal;
      switch (key) {
        case 'product':
          aVal = (a.Product?.name || '').toLowerCase();
          bVal = (b.Product?.name || '').toLowerCase();
          break;
        case 'quantity':
          aVal = Number(a.quantity) || 0;
          bVal = Number(b.quantity) || 0;
          break;
        case 'date_logged':
          aVal = a.date_logged ? new Date(a.date_logged).getTime() : 0;
          bVal = b.date_logged ? new Date(b.date_logged).getTime() : 0;
          break;
        case 'production_date':
          aVal = a.Batch?.production_date ? new Date(a.Batch.production_date).getTime() : 0;
          bVal = b.Batch?.production_date ? new Date(b.Batch.production_date).getTime() : 0;
          break;
        default:
          aVal = 0; bVal = 0;
      }
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
  }, [logs, sortConfig]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatQty = (ing) => {
    if (ing.quantityInput == null) return '—';
    const n = Number(ing.quantityInput);
    const uom = ing.uomInput || '';
    return `${Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 3 }) : '—'} ${uom}`.trim();
  };

  const statusBadge = (status) => {
    const map = { ENTERED: 'success', MISSING: 'danger', EXCLUDED: 'secondary' };
    return (
      <span className={`badge bg-${map[status] || 'secondary'}`} style={{ fontSize: 11 }}>
        {status}
      </span>
    );
  };

  // When productId was passed, ingredients have inProductRecipe flag — split accordingly
  const hasRecipeInfo = displayIngredients.some(i => i.inProductRecipe !== undefined);
  const recipeIngredients = hasRecipeInfo
    ? displayIngredients.filter(i => i.inProductRecipe)
    : displayIngredients;
  const otherIngredients = hasRecipeInfo
    ? displayIngredients.filter(i => !i.inProductRecipe)
    : [];

  // Count how many ingredients are still MISSING after dedup
  const missingCount = useMemo(
    () => displayIngredients.filter(i => i.status === 'MISSING').length,
    [displayIngredients]
  );

  return (
    <div className="container mt-5">
      <h2 className="text-center">Production Log</h2>

      {/* Single filter bar */}
      <form onSubmit={handleFilterSubmit} className="card mt-4 mb-3">
        <div className="card-body py-3">
          <div className="row align-items-end g-2">
            <div className="col-md-3">
              <label className="form-label mb-1 small">Product</label>
              <select
                className="form-select"
                value={inputProductId}
                onChange={e => setInputProductId(e.target.value)}
              >
                <option value="">All products</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1 small">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={inputStartDate}
                onChange={e => setInputStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1 small">End Date</label>
              <input
                type="date"
                className="form-control"
                value={inputEndDate}
                onChange={e => setInputEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1 small">Total Units</label>
              <input
                type="text"
                className="form-control"
                readOnly
                value={totalQtyLoading ? '…' : totalQty != null ? Number(totalQty).toLocaleString() : '—'}
                style={{ background: '#f8f9fa', cursor: 'default' }}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label mb-1 small">Lot Code / Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search…"
                value={inputSearch}
                onChange={e => setInputSearch(e.target.value)}
              />
            </div>
            <div className="col-md-1">
              <label className="form-label mb-1 small d-block">&nbsp;</label>
              <button type="submit" className="btn btn-primary w-100">Apply</button>
            </div>
          </div>
        </div>
      </form>

      {/* Chart — driven by applied filters */}
      <ProductionTrendChart startDate={startDate} endDate={endDate} productId={productId} />

      {/* Logs Table */}
      <table className="table table-bordered table-responsive-sm">
        <thead>
          <tr>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('product')}>
              Product{getSortArrow('product')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('quantity')}>
              Quantity{getSortArrow('quantity')}
            </th>
            <th>Lot Code</th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date_logged')}>
              Date Logged{getSortArrow('date_logged')}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('production_date')}>
              Production Date{getSortArrow('production_date')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedLogs.map((log) => (
            <tr key={log.id} onClick={() => handleRowClick(log)} style={{ cursor: 'pointer' }}>
              <td>{log.Product?.name || 'Unknown Product'}</td>
              <td>{log.quantity}</td>
              <td>{log.Batch?.lotCode || 'N/A'}</td>
              <td>{new Date(log.date_logged).toLocaleString()}</td>
              <td>{log.Batch?.production_date || 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center my-3">
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentPage((prev) => prev - 1)}
          disabled={currentPage === 1}
        >
          &laquo; Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentPage((prev) => prev + 1)}
          disabled={currentPage >= totalPages}
        >
          Next &raquo;
        </button>
      </div>

      {/* Ingredients Modal */}
      {isModalOpen && selectedLog && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            className="modal-dialog modal-lg"
            role="document"
            style={{ marginTop: '80px' }}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>
                <div>
                  <h5 className="modal-title mb-0">
                    Ingredients — Lot Code: <strong>{selectedLog.Batch?.lotCode || 'N/A'}</strong>
                  </h5>
                  <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                    {selectedLog.Product?.name} · Production: {selectedLog.Batch?.production_date || '—'}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  style={{ filter: 'invert(1)' }}
                  onClick={closeModal}
                />
              </div>

              <div className="modal-body p-0">
                {displayIngredients.length === 0 ? (
                  <div className="p-4 text-muted">No ingredients found for this batch.</div>
                ) : (
                  <>
                    {/* Missing ingredients warning */}
                    {missingCount > 0 && (
                      <div className="alert alert-warning rounded-0 mb-0" style={{ fontSize: 13 }}>
                        ⚠ <strong>{missingCount} ingredient{missingCount > 1 ? 's' : ''}</strong> {missingCount > 1 ? 'are' : 'is'} missing a lot code.
                      </div>
                    )}

                    {/* Recipe label when split is available */}
                    {hasRecipeInfo && (
                      <div className="px-3 pt-3 pb-1" style={{ fontSize: 12, fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Recipe ingredients for {selectedLog.Product?.name}
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ paddingLeft: 16 }}>Ingredient</th>
                            <th>Lot Code</th>
                            <th>Quantity Used</th>
                            <th style={{ width: 100 }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recipeIngredients.map((ing) => {
                            const docLog = receivingDocsMap[`${ing.ingredientId}:${ing.ingredientLotCode}`];
                            const checksPassed = docLog ? (docLog.CheckResults || []).filter(c => c.passed).length : 0;
                            const checksTotal = docLog ? (docLog.CheckResults || []).length : 0;
                            const docs = docLog ? (docLog.Documents || []) : [];
                            return (
                              <React.Fragment key={ing.id}>
                                <tr>
                                  <td style={{ paddingLeft: 16, fontWeight: 600 }}>
                                    {ing.Ingredient?.name || `Ingredient #${ing.ingredientId}`}
                                  </td>
                                  <td>
                                    {ing.ingredientLotCode
                                      ? <code style={{ fontSize: 13 }}>{ing.ingredientLotCode}</code>
                                      : <span className="text-danger" style={{ fontSize: 12 }}>Not recorded</span>
                                    }
                                  </td>
                                  <td style={{ fontSize: 13 }}>{formatQty(ing)}</td>
                                  <td>{statusBadge(ing.status)}</td>
                                </tr>
                                {docLog && (
                                  <tr>
                                    <td colSpan={4} className="p-0">
                                      <div className="px-3 py-2 border-top d-flex flex-wrap align-items-center gap-3" style={{ fontSize: 12, background: '#f8f9fa' }}>
                                        {docLog.supplierName && (
                                          <span><span className="text-muted">Supplier:</span> <strong>{docLog.supplierName}</strong></span>
                                        )}
                                        {docLog.deliveryDate && (
                                          <span><span className="text-muted">Delivered:</span> <strong>{new Date(docLog.deliveryDate).toLocaleDateString()}</strong></span>
                                        )}
                                        {docLog.isTemperatureControlled && docLog.receivedTemperature != null && (
                                          <span><span className="text-muted">Recv Temp:</span> <strong>{docLog.receivedTemperature}°C{docLog.isFrozen ? ' ❄' : ''}</strong></span>
                                        )}
                                        {checksTotal > 0 && (
                                          <span className={checksPassed === checksTotal ? 'text-success fw-semibold' : 'text-warning fw-semibold'}>
                                            {checksPassed === checksTotal ? '✓' : '⚠'} {checksPassed}/{checksTotal} receiving checks
                                          </span>
                                        )}
                                        {docs.map(doc => {
                                          const label = { bol: 'BOL', receipt: 'Receipt', other: 'Doc' }[doc.documentType] || 'Doc';
                                          return (
                                            <button
                                              key={doc.id}
                                              className="btn btn-outline-secondary btn-sm py-0 px-2"
                                              style={{ fontSize: 11 }}
                                              onClick={async () => {
                                                try {
                                                  const { url } = await ingredientInventoryService.getDocumentUrlById(docLog.id, doc.id);
                                                  window.open(url, '_blank');
                                                } catch { alert('Could not load document.'); }
                                              }}
                                            >
                                              📎 {label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Other batch ingredients (not in this product's recipe) */}
                    {otherIngredients.length > 0 && (
                      <details className="border-top">
                        <summary className="px-3 py-2" style={{ fontSize: 12, fontWeight: 700, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', userSelect: 'none' }}>
                          Other batch ingredients ({otherIngredients.length}) — not in {selectedLog.Product?.name} recipe
                        </summary>
                        <div className="table-responsive">
                          <table className="table table-sm align-middle mb-0" style={{ opacity: 0.75 }}>
                            <tbody>
                              {otherIngredients.map((ing) => (
                                <tr key={ing.id}>
                                  <td style={{ paddingLeft: 16, fontWeight: 500 }}>{ing.Ingredient?.name || `Ingredient #${ing.ingredientId}`}</td>
                                  <td>{ing.ingredientLotCode ? <code style={{ fontSize: 13 }}>{ing.ingredientLotCode}</code> : <span className="text-muted small">—</span>}</td>
                                  <td style={{ fontSize: 13 }}>{formatQty(ing)}</td>
                                  <td>{statusBadge(ing.status)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}

                    {/* Summary footer */}
                    <div className="px-3 py-2 border-top d-flex gap-3" style={{ fontSize: 12, color: '#6c757d' }}>
                      <span>{displayIngredients.filter(i => i.status === 'ENTERED').length} entered</span>
                      {missingCount > 0 && <span className="text-danger">{missingCount} missing</span>}
                      {displayIngredients.filter(i => i.status === 'EXCLUDED').length > 0 && (
                        <span>{displayIngredients.filter(i => i.status === 'EXCLUDED').length} excluded</span>
                      )}
                      <span className="ms-auto">
                        {displayIngredients.length} ingredient{displayIngredients.length !== 1 ? 's' : ''} total
                      </span>
                    </div>
                  </>
                )}

                {canViewChecklists && (
                  <div className="px-3">
                    <VisualInspectionsList inspections={visualInspections} />
                  </div>
                )}
              </div>

              {(canViewSQF || canViewChecklists) && (
                <div className="modal-footer">
                  {canViewSQF && (
                    ccpRun ? (
                      <Link
                        to={`/ccp/baking/verify/${ccpRun.id}`}
                        className="btn btn-outline-primary btn-sm"
                        onClick={closeModal}
                      >
                        View SQF Report
                      </Link>
                    ) : (
                      <button className="btn btn-outline-secondary btn-sm" disabled title="No CCP run found for this batch">
                        View SQF Report
                      </button>
                    )
                  )}
                  {canViewChecklists && selectedLog.batchId && selectedLog.productId && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => setShowVisualInspectionModal(true)}>
                      📷 Start Visual Inspection
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={closeModal}>Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showVisualInspectionModal && selectedLog && (
        <StartVisualInspectionModal
          defaultBatchId={selectedLog.batchId}
          batchLabel={selectedLog.Batch?.lotCode || `Batch #${selectedLog.batchId}`}
          productOptions={[{ id: selectedLog.productId, name: selectedLog.Product?.name || '' }]}
          onClose={() => setShowVisualInspectionModal(false)}
        />
      )}
    </div>
  );
}

export default ProductionLog;